import type { IProductEnrichment } from '~~/lib/ContentfulService';

export const PDP_ENRICHMENT_TAB_ID_PREFIX = 'tab-product-enrichment-';

export const DEFAULT_ENRICHMENT_TAB_POSITION = 1;
export const DEFAULT_ENRICHMENT_TAB_SORTING_PRIO = 0;

export interface ProductEnrichmentTabGroup {
  /** `TabPanel` uid; deep link anchors are `#tab-panel-${id}` / `#tab-item-${id}`. */
  id: string;
  /** Localized tab title, `undefined` when no enrichment of the group sets one. */
  title: string | undefined;
  position: number;
  sortingPrio: number;
  enrichments: IProductEnrichment[];
}

const hasTabContent = (enrichment: IProductEnrichment | undefined): boolean =>
  Boolean(enrichment?.fields?.contentSlotTabElement?.length);

/**
 * Groups product enrichments into PDP tabs.
 *
 * Enrichments only share a tab when tab title, tab position and sorting prio are
 * identical; any difference in those three fields yields its own tab (DBP-4500).
 * The group id is derived from the first enrichment of the group, so anchors
 * stay stable as long as that entry matches the product.
 */
export function groupProductEnrichmentTabs(
  productEnrichments: readonly IProductEnrichment[]
): ProductEnrichmentTabGroup[] {
  const groupsByKey = new Map<string, ProductEnrichmentTabGroup>();

  for (const enrichment of productEnrichments) {
    if (!hasTabContent(enrichment) || !enrichment.sys?.id) {
      continue;
    }

    const title = enrichment.fields.contentSlotTabTitle || undefined;
    const position =
      enrichment.fields.contentSlotTabPosition ??
      DEFAULT_ENRICHMENT_TAB_POSITION;
    const sortingPrio =
      enrichment.fields.sortingPrio ?? DEFAULT_ENRICHMENT_TAB_SORTING_PRIO;

    const key = JSON.stringify([title ?? null, position, sortingPrio]);
    const existingGroup = groupsByKey.get(key);

    if (existingGroup) {
      existingGroup.enrichments.push(enrichment);
      continue;
    }

    groupsByKey.set(key, {
      id: `${PDP_ENRICHMENT_TAB_ID_PREFIX}${enrichment.sys.id}`,
      title,
      position,
      sortingPrio,
      enrichments: [enrichment]
    });
  }

  // Enrichments arrive pre-sorted by descending sorting prio, so only the tab
  // position needs to be applied here. Array.prototype.sort is stable, which
  // keeps that incoming order for tabs sharing a position.
  return [...groupsByKey.values()].sort((a, b) => a.position - b.position);
}
